import { useCallback, useEffect, useState } from "react";
import { chatService } from "api/chat-service";
import { fileService } from "api/file-service";
import { useDraftStorage } from "./useDraftStorage";

const truncateComposerFileName = (str) => {
  if (str.length <= 15) return str;
  return `${str.slice(0, 6)}...${str.slice(-6)}`;
};

export const useChatComposer = ({
  chatId,
  userId,
  sendButtonRef,
  hiddenFileInput,
  shouldAutoScrollRef,
  handleError,
  handleClickVariant,
  refreshLatestMessages,
  scrollToBottom,
  handleMarkChatIsRead,
  cancelReply,
  replyingTo,
}) => {
  const { draft, saveDraft, clearDraft } = useDraftStorage({ chatId, userId });
  const [newMessage, setNewMessage] = useState("");
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [audioFile, setAudioFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [sendButtonWidth, setSendButtonWidth] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);

  useEffect(() => {
    setNewMessage(draft);
  }, [draft]);

  const setNewMessageWithDraft = useCallback(
    (value) => {
      setNewMessage(value);
      saveDraft(value);
    },
    [saveDraft]
  );

  useEffect(() => {
    if (sendButtonRef?.current) {
      setSendButtonWidth(sendButtonRef.current?.offsetWidth + 20);
    }
  }, [sendButtonRef]);

  const handleStartEdit = useCallback((msg) => {
    setEditingMessage({ id: msg.id, text: msg.text });
    setNewMessage(msg.text);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingMessage(null);
    setNewMessageWithDraft("");
  }, [setNewMessageWithDraft]);

  const getFileType = useCallback(
      (fileType) => {
        if (fileType.startsWith("image/")) return "photo";
        if (fileType.startsWith("video/")) return "video";
        if (
            fileType === "application/pdf" ||
            fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        )
          return "document";
        if (fileType.startsWith("audio/")) return "audio";
        handleClickVariant("No support this format", "error");
        return null;
      },
      [handleClickVariant]
  );

  const handleChangeFile = useCallback(
      async (event) => {
        try {
          let fileType = getFileType(event.target.files[0].type);
          if (event.target.files && fileType) {
            let nextFile = event.target.files[0];
            const smallFileSize = 5;
            const bigFileSize = 20;
            const megabytes = nextFile?.size / (1024 * 1024);
            if (fileType === "photo" && megabytes > smallFileSize) {
              fileType = null;
              nextFile = null;
              handleClickVariant("File must be lower or equal 5MB", "error");
              return;
            }
            if (
                (fileType === "video" || fileType === "document" || fileType === "audio") &&
                megabytes > bigFileSize
            ) {
              fileType = null;
              nextFile = null;
              handleClickVariant("File must be lower or equal 20MB", "error");
              return;
            }
            setIsLoadingFile(true);
            setFileName(nextFile.name);
            const response = await fileService.uploadFile({
              file: nextFile,
              folder: "admins",
              type: fileType,
            });
            setFile(response);
            setIsLoadingFile(false);
          }
        } catch (error) {
          if (error) {
            setIsLoadingFile(false);
            handleError(error);
          }
        } finally {
          setIsLoadingFile(false);
          if (event?.target) event.target.value = "";
        }
        event.target.value = "";
      },
      [getFileType, handleClickVariant, handleError]
  );

  const renderFileLabel = useCallback(
      (fileType) => {
        switch (fileType) {
          case "photo":
          case "document":
          case "video":
          case "audio":
          case "voice":
            return truncateComposerFileName(fileName);
          default:
            return fileName;
        }
      },
      [fileName]
  );

  const openFilePicker = useCallback(() => {
    if (hiddenFileInput.current) hiddenFileInput.current.click();
  }, [hiddenFileInput]);

  const clearAttachedFile = useCallback(() => setFile(null), []);

  const handleSendMessage = useCallback(async () => {
    if (editingMessage) {
      try {
        await chatService.editMessage(chatId, editingMessage.id, newMessage);
        clearDraft();
        setEditingMessage(null);
        await refreshLatestMessages();
      } catch (error) {
        handleError(error);
      }
      return;
    }

    if (isRecording) return;

    const replyPayload = replyingTo ? { parentId: replyingTo.id } : {};

    if (audioFile) {
      setIsLoading(true);
      try {
        fileService
            .uploadFile({ file: audioFile, folder: "admins", type: "audio" })
            .then(async (res) => {
              if (res?.source) {
                await chatService.sendMessage(chatId, {
                  message: res.source,
                  messageType: "voice",
                  ...replyPayload,
                });
                cancelReply();
                shouldAutoScrollRef.current = true;
                await refreshLatestMessages();
                scrollToBottom();
                handleMarkChatIsRead();
                clearDraft();
              }
            })
            .catch((error) => {
              handleError(error);
            })
            .finally(() => {
              setAudioFile(null);
              setIsLoading(false);
            });
      } catch (error) {
        handleError(error);
      }
      return;
    }

    if (file) {
      try {
        await chatService.sendMessage(chatId, {
          message: file.source,
          messageType: file.type,
          ...replyPayload,
        });
        cancelReply();
        shouldAutoScrollRef.current = true;
        await refreshLatestMessages();
        setFile(null);
        setFileName("");
        clearDraft();
        scrollToBottom();
        handleMarkChatIsRead();
      } catch (error) {
        handleError(error);
      }
      return;
    }

    if (!newMessage.trim() || !chatId) return;

    if (newMessage?.length > 3500) {
      handleClickVariant("Max message length is 3500", "error");
      return;
    }

    try {
      await chatService.sendMessage(chatId, {
        message: newMessage,
        messageType: "text",
        ...replyPayload,
      });
      clearDraft();
      cancelReply();
      shouldAutoScrollRef.current = true;
      await refreshLatestMessages();
      scrollToBottom();
      handleMarkChatIsRead();
    } catch (error) {
      handleError(error);
    }
  }, [
    audioFile,
    cancelReply,
    chatId,
    clearDraft,
    editingMessage,
    file,
    handleClickVariant,
    handleError,
    handleMarkChatIsRead,
    isRecording,
    newMessage,
    refreshLatestMessages,
    replyingTo,
    scrollToBottom,
    shouldAutoScrollRef,
  ]);

  return {
    newMessage,
    setNewMessage: setNewMessageWithDraft,
    isLoadingFile,
    file,
    fileName,
    audioFile,
    setAudioFile,
    isRecording,
    setIsRecording,
    sendButtonWidth,
    isLoading,
    editingMessage,
    handleStartEdit,
    handleCancelEdit,
    handleSendMessage,
    handleChangeFile,
    renderFileLabel,
    openFilePicker,
    clearAttachedFile,
  };
};
